import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Container, Typography, TextField, Button, Grid, Card, CardContent,
    Stack, Chip, CircularProgress, Avatar, Divider, List, ListItem,
    ListItemIcon, ListItemText, Paper, useTheme, useMediaQuery, Tooltip,
    FormControl, InputLabel, Select, MenuItem, Slider, Alert
} from '@mui/material';
import {
    FaLeaf, FaSearch, FaRobot, FaTemperatureHigh, FaTint, FaSun,
    FaWater, FaRulerHorizontal, FaSeedling, FaHandHoldingHeart, FaLightbulb,
    FaArrowLeft, FaShoppingCart, FaMicrochip, FaFlask, FaBoxOpen, FaTractor,
    FaGlobeAmericas, FaUtensils, FaHeartbeat, FaExclamationTriangle, FaHome, FaTree
} from 'react-icons/fa';
import { getPlantGuide, applyGuideToDevice } from '../../services/plantAI';
import { useApp } from '../../context/AppContext';
import Navbar from '../Common/Navbar';
import { api } from '../../services/api';

// Mapeia cada linha da resposta da IA para um ícone, para uma exibição rica.
function iconForLine(line) {
    if (line.includes('🌡️') || line.toLowerCase().includes('temperatura')) return <FaTemperatureHigh color="#FF7043" />;
    if (line.includes('💧') || line.toLowerCase().includes('umidade')) return <FaTint color="#29B6F6" />;
    if (line.includes('☀️') || line.toLowerCase().includes('luz')) return <FaSun color="#FFCA28" />;
    if (line.includes('🚿') || line.toLowerCase().includes('rega')) return <FaWater color="#26C6DA" />;
    if (line.includes('📏') || line.toLowerCase().includes('espaço') || line.toLowerCase().includes('espaco')) return <FaRulerHorizontal color="#AB47BC" />;
    if (line.includes('🪴') || line.toLowerCase().includes('substrato') || line.toLowerCase().includes('solo')) return <FaSeedling color="#66BB6A" />;
    if (line.includes('🌾') || line.toLowerCase().includes('colheita')) return <FaHandHoldingHeart color="#EC407A" />;
    if (line.includes('💡') || line.toLowerCase().includes('dica')) return <FaLightbulb color="#FFEE58" />;
    if (line.includes('🌱') || line.toLowerCase().includes('dificuldade')) return <FaLeaf color="#4CAF50" />;
    return <FaLeaf color="#4CAF50" />;
}

function AnswerCard({ answer, source, plant }) {
    const lines = (answer || '').split('\n').filter(l => l.trim().length > 0);
    const cleanLine = (line) => line
        .replace(/^\s*=+\s*/, '')
        .replace(/\s*=+\s*$/, '')
        .replace(/^▣\s*/, '')
        .trim();
    const isSection = (line) => {
        const value = cleanLine(line);
        return /^={2,}/.test(line.trim()) || /^(🌿|🍽️|💚|⚠️|🪴|🌾|💡)\s*[^:]+:?$/.test(value) || /^(Cultivo|Usos|Benefícios|Cuidados|Nutrição|Características|Fonte):?$/i.test(value);
    };
    return (
        <Card sx={{ mt: 3, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(76,175,80,0.25)', borderRadius: 3 }}>
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}><FaRobot color="#fff" /></Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {plant ? plant.nome : 'Guia de Cultivo (IA)'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Fonte: {source === 'wikipedia' ? 'Wikipédia em português' : source === 'offline-fallback' ? 'Modo offline' : 'Pesquisa online'}
                        </Typography>
                    </Box>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                    {lines.map((line, i) => {
                        const cleaned = cleanLine(line).replace(/^[🌱🌡️💧☀️🚿📏🪴🌾💡🍽️💚⚠️🔎📚\s]+/, '').trim();
                        if (isSection(line)) {
                            return (
                                <ListItem key={i} sx={{ mt: i ? 1.5 : 0, py: 0.75, px: 1, borderLeft: '3px solid', borderColor: 'primary.main', background: 'rgba(76,175,80,0.08)' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.light' }}>{cleaned.replace(/:$/, '')}</Typography>
                                </ListItem>
                            );
                        }
                        return (
                            <ListItem key={i} alignItems="flex-start" sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>{iconForLine(line)}</ListItemIcon>
                                <ListItemText primary={cleaned} primaryTypographyProps={{ sx: { whiteSpace: 'pre-wrap', lineHeight: 1.6 } }} />
                            </ListItem>
                        );
                    })}
                </List>
            </CardContent>
        </Card>
    );
}

// Card de informação genérico (ícone + título + texto)
function InfoCard({ icon: Icon, color, title, children }) {
    return (
        <Card sx={{ height: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 }}>
            <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ color }}>{Icon}</Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
                </Stack>
                {children}
            </CardContent>
        </Card>
    );
}

// Seletor de tamanho do vaso (L) com cálculo em tempo real
function VasoSelector({ vasoL, setVasoL, calc }) {
    return (
        <Paper elevation={0} sx={{ p: 2, mt: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <FaRulerHorizontal color="#AB47BC" size={20} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tamanho do vaso</Typography>
                <Chip label={`${vasoL} L`} color="secondary" size="small" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
                Ao alterar o vaso, rega, colheita e espaçamento são recalculados automaticamente (proporcional ao volume).
            </Typography>
            <Slider
                value={vasoL}
                min={1} max={60} step={1}
                onChange={(e, v) => setVasoL(v)}
                valueLabelDisplay="auto"
                sx={{ mt: 2, color: 'secondary.main' }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                {[3, 5, 10, 15, 20, 30, 50].map(v => (
                    <Chip key={v} label={`${v} L`} size="small" variant="outlined"
                        onClick={() => setVasoL(v)} sx={{ cursor: 'pointer' }} />
                ))}
            </Stack>
            {calc && (
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip icon={<FaWater />} label={`Rega: ${calc.irrigationAmount} mL`} color="info" size="small" />
                    <Chip icon={<FaHandHoldingHeart />}
                        label={`Colheita: ${calc.colheitaAprox != null ? calc.colheitaAprox + ' g' : 'ornamental'}`}
                        color="secondary" size="small" />
                    <Chip icon={<FaRulerHorizontal />} label={`Espaço: ~${calc.espacoAprox} cm`} size="small" />
                </Stack>
            )}
        </Paper>
    );
}

// Botões para: (1) adicionar a planta às "minhas plantas" e
// (2) definir no controlador (cria a planta + calcula média com as demais).
function ApplyToDevice({ plant, mode, vasoL, environment, deviceId }) {
    const [applying, setApplying] = useState(false);
    const [adding, setAdding] = useState(false);
    const [msg, setMsg] = useState(null);

    // Adiciona a planta do guia às plantas ativas do usuário (device)
    const addToPlants = async () => {
        if (!deviceId) { setMsg({ type: 'error', text: 'Selecione um dispositivo nas Configurações.' }); return; }
        setAdding(true); setMsg(null);
        try {
            await api.post('/plants', {
                deviceId,
                name: plant.nome,
                guideKey: plant.key,
                guideMode: mode || null,
                vasoL: vasoL || null,
                environment: environment || 'indoor',
                wateringAmount: plant.vaso ? plant.vaso.irrigationAmount : null,
                status: 'active'
            });
            setMsg({ type: 'success', text: 'Planta adicionada às suas plantas ativas!' });
        } catch (e) {
            setMsg({ type: 'error', text: 'Erro ao adicionar planta.' });
        } finally { setAdding(false); }
    };

    // Define no controlador: cria a planta e calcula a MÉDIA com as demais ativas
    const apply = async () => {
        if (!deviceId) { setMsg({ type: 'error', text: 'Selecione um dispositivo nas Configurações.' }); return; }
        setApplying(true); setMsg(null);
        try {
            const r = await applyGuideToDevice(deviceId, plant.key || plant.nome, mode, vasoL, environment, true);
            if (r.success) {
                const avg = r.average;
                const txt = avg && avg.plantsCount > 1
                    ? `Controlador ajustado! Média de ${avg.plantsCount} plantas: rega ${avg.irrigationAmount}mL, luz ${avg.lightingProfile}, umidade ${avg.targetHumidity}%, temp ${avg.targetTemp}°C.`
                    : 'Configurações do controlador ajustadas automaticamente!';
                setMsg({ type: 'success', text });
            } else setMsg({ type: 'error', text: r.error || 'Falha ao aplicar.' });
        } catch (e) {
            setMsg({ type: 'error', text: 'Erro ao aplicar ao controlador.' });
        } finally { setApplying(false); }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" startIcon={<FaSeedling />}
                    onClick={addToPlants} disabled={adding || !deviceId}
                    sx={{ whiteSpace: 'nowrap' }}>
                    {adding ? 'Adicionando...' : 'Adicionar às minhas plantas'}
                </Button>
                <Button variant="contained" color="secondary" startIcon={<FaMicrochip />}
                    onClick={apply} disabled={applying || !deviceId}
                    sx={{ whiteSpace: 'nowrap' }}>
                    {applying ? 'Aplicando...' : 'Definir no controlador (média)'}
                </Button>
            </Stack>
            {!deviceId && <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 1 }}>Defina um dispositivo ativo primeiro.</Typography>}
            {msg && <Alert severity={msg.type} sx={{ mt: 1 }}>{msg.text}</Alert>}
        </Box>
    );
}

export default function CultivationGuide() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { selectedDeviceId } = useApp();
    const location = useLocation();
    // Se o guia foi aberto a partir do dashboard, mantém o cabeçalho (Navbar) do dashboard.
    const fromDashboard = !!(location.state && location.state.from === 'dashboard');
    const backTo = fromDashboard ? '/dashboard' : '/';

    const [query, setQuery] = useState('');
    const [mode, setMode] = useState(null); // 'fotoperiodo' | 'automatica'
    const [environment, setEnvironment] = useState('indoor'); // 'indoor' | 'outdoor'
    const [vasoL, setVasoL] = useState(10);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const search = async (q, m, v, env) => {
        const term = (q ?? query).trim();
        if (!term) return;
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const data = await getPlantGuide(term, m ?? mode, v ?? vasoL, env ?? environment);
            setResult(data);
            // Se a planta tem dois modos e nenhum foi escolhido, mantém nulo p/ mostrar colunas
            if (data.found && data.plant.modo && !(m ?? mode)) setMode(null);
        } catch (e) {
            setError('Não foi possível consultar o guia agora. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const onKey = (e) => { if (e.key === 'Enter') search(); };

    // Ao alterar o vaso, recalcula imediatamente (se já há resultado)
    const changeVaso = (v) => {
        setVasoL(v);
        if (result && result.found) search(result.plant.key || query, mode, v, environment);
    };

    // Ao trocar o ambiente (indoor/outdoor), recalcula imediatamente
    const changeEnvironment = (env) => {
        setEnvironment(env);
        if (result && result.found) search(result.plant.key || query, mode, vasoL, env);
    };

    const p = result && result.found ? result.plant : null;
    const hasMode = p && p.modo; // cannabis etc.

    return (
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh' }}>
            {/* Mantém o cabeçalho (Navbar) do Dashboard quando acessado por lá */}
            {fromDashboard && <Navbar />}
            {/* Header (usado quando acessado pela Landing ou direto) */}
            {!fromDashboard && (
            <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(10px)', background: 'rgba(18,18,18,0.8)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => navigate(backTo)}>
                        <FaLeaf size={26} color="#4CAF50" />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>AutoGrow BR</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={() => navigate(backTo)}>
                            {backTo === '/dashboard' ? 'Voltar ao Dashboard' : 'Início'}
                        </Button>
                        <Button variant="contained" startIcon={<FaShoppingCart />} onClick={() => navigate('/comprar')}>
                            Comprar controlador
                        </Button>
                    </Stack>
                </Container>
            </Box>
            )}

            <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
                {/* Título */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                        <FaRobot color="#fff" size={26} />
                    </Avatar>
                    <Box>
                        <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 800 }}>
                            Guia de Cultivo com IA
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pesquise qualquer planta e receba informações confiáveis para cuidar melhor do seu cultivo.
                        </Typography>
                    </Box>
                </Stack>

                {/* Busca */}
                <Paper elevation={0} sx={{ p: 2, mt: 3, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 3 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            placeholder="Ex.: tomate, manjericão, cannabis, orquídea..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKey}
                            InputProps={{ startAdornment: <FaSearch style={{ marginRight: 8, color: '#888' }} /> }}
                        />
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => search()}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FaSearch />}
                            sx={{ minWidth: 140, whiteSpace: 'nowrap' }}
                        >
                            {loading ? 'Pesquisando' : 'Pesquisar planta'}
                        </Button>
                    </Stack>
                </Paper>

                {/* Erro */}
                {error && (<Typography color="error" sx={{ mt: 2 }}>{error}</Typography>)}

                {/* Resultado */}
                {result && result.found && p && (
                    <>
                        <AnswerCard answer={result.answer} source={result.source} plant={p} />

                        {/* Seletor de ambiente: Indoor (controlado) vs Outdoor (sol natural) */}
                        <Paper elevation={0} sx={{ p: 2, mt: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Ambiente de cultivo
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button variant={environment === 'indoor' ? 'contained' : 'outlined'}
                                    startIcon={<FaHome />}
                                    onClick={() => changeEnvironment('indoor')}>
                                    Indoor (estufa/controle)
                                </Button>
                                <Button variant={environment === 'outdoor' ? 'contained' : 'outlined'}
                                    startIcon={<FaTree />}
                                    onClick={() => changeEnvironment('outdoor')}>
                                    Outdoor (sol natural)
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {environment === 'indoor'
                                    ? 'Indoor: luz e clima controlados (LED, VPD, rega mais precisa e menor perda por evaporação).'
                                    : 'Outdoor: sol natural e clima regional; rega maior (evaporação) e ciclo depende da estação.'}
                            </Typography>
                        </Paper>

                        {/* Cálculos de vaso ficam disponíveis para plantas do catálogo AutoGrow. */}
                        {p.vaso && <VasoSelector vasoL={vasoL} setVasoL={changeVaso} calc={p.vaso} />}

                        {/* Modo fotoperíodo / automática (quando aplicável) */}
                        {hasMode && (
                            <Paper elevation={0} sx={{ p: 2, mt: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                    Tipo de cultivo
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button variant={!mode ? 'contained' : 'outlined'}
                                        onClick={() => { setMode(null); search(p.key || query, null, vasoL); }}>
                                        Ambos (comparar)
                                    </Button>
                                    <Button variant={mode === 'fotoperiodo' ? 'contained' : 'outlined'}
                                        onClick={() => { setMode('fotoperiodo'); search(p.key || query, 'fotoperiodo', vasoL); }}>
                                        Fotoperíodo
                                    </Button>
                                    <Button variant={mode === 'automatica' ? 'contained' : 'outlined'}
                                        onClick={() => { setMode('automatica'); search(p.key || query, 'automatica', vasoL); }}>
                                        Automática
                                    </Button>
                                </Stack>
                            </Paper>
                        )}

                        {/* Colunas: fotoperíodo vs automática (ou coluna única) */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {hasMode && (!mode || mode === 'fotoperiodo') && (
                                <Grid item xs={12} md={hasMode && !mode ? 6 : 12}>
                                    <InfoCard icon={<FaSeedling color="#66BB6A" />} color="#66BB6A" title="Fotoperíodo">
                                        <Typography variant="body2">{p.modo.fotoperiodo.desc}</Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}><b>Ciclo:</b> {p.modo.fotoperiodo.ciclo}</Typography>
                                        <Typography variant="body2"><b>Luz:</b> {p.modo.fotoperiodo.luz}</Typography>
                                        <Typography variant="body2"><b>Rega:</b> {p.modo.fotoperiodo.rega}</Typography>
                                        <Typography variant="body2"><b>Colheita:</b> {p.modo.fotoperiodo.colheitaQtd}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                            {hasMode && (!mode || mode === 'automatica') && (
                                <Grid item xs={12} md={hasMode && !mode ? 6 : 12}>
                                    <InfoCard icon={<FaSeedling color="#26C6DA" />} color="#26C6DA" title="Automática">
                                        <Typography variant="body2">{p.modo.automatica.desc}</Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}><b>Ciclo:</b> {p.modo.automatica.ciclo}</Typography>
                                        <Typography variant="body2"><b>Luz:</b> {p.modo.automatica.luz}</Typography>
                                        <Typography variant="body2"><b>Rega:</b> {p.modo.automatica.rega}</Typography>
                                        <Typography variant="body2"><b>Colheita:</b> {p.modo.automatica.colheitaQtd}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                        </Grid>

                        {/* Colheita aproximada (calculada pelo vaso) */}
                        {p.vaso && p.vaso.colheitaAprox != null && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                <b>Colheita aproximada ({p.vaso.litros} L):</b> {p.vaso.colheitaAprox} g
                                {p.vaso.mode ? ` (${p.vaso.mode})` : ''}
                            </Alert>
                        )}

                        {/* Nutrição: adubos, fertilizantes, substratos */}
                        {p.nutricao && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={4}>
                                    <InfoCard icon={<FaBoxOpen color="#AB47BC" />} color="#AB47BC" title="Substrato">
                                        <Typography variant="body2">{p.nutricao.substrato}</Typography>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InfoCard icon={<FaTractor color="#66BB6A" />} color="#66BB6A" title="Adubo de base">
                                        <Typography variant="body2">{p.nutricao.aduboBase}</Typography>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InfoCard icon={<FaFlask color="#FFCA28" />} color="#FFCA28" title="Fertilizante">
                                        <Typography variant="body2">{p.nutricao.fertilizante}</Typography>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity="info">{p.nutricao.observacoes}</Alert>
                                </Grid>
                            </Grid>
                        )}

                        {/* Origem, usos, benefícios e contra-indicações */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {p.paisOrigem && (
                                <Grid item xs={12} md={6}>
                                    <InfoCard icon={<FaGlobeAmericas color="#42A5F5" />} color="#42A5F5" title="País de origem">
                                        <Typography variant="body2">{p.paisOrigem}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                            {p.usos && (
                                <Grid item xs={12} md={6}>
                                    <InfoCard icon={<FaUtensils color="#FFA726" />} color="#FFA726" title="Usos">
                                        <Typography variant="body2">{p.usos}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                            {p.beneficios && (
                                <Grid item xs={12} md={6}>
                                    <InfoCard icon={<FaHeartbeat color="#66BB6A" />} color="#66BB6A" title="Benefícios">
                                        <Typography variant="body2">{p.beneficios}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                            {p.contraIndicacoes && (
                                <Grid item xs={12} md={6}>
                                    <InfoCard icon={<FaExclamationTriangle color="#EF5350" />} color="#EF5350" title="Contra-indicações / Malefícios">
                                        <Typography variant="body2">{p.contraIndicacoes}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                            {p.luzAmbiente && (
                                <Grid item xs={12}>
                                    <InfoCard icon={environment === 'outdoor' ? <FaTree color="#66BB6A" /> : <FaHome color="#42A5F5" />}
                                        color={environment === 'outdoor' ? '#66BB6A' : '#42A5F5'}
                                        title={`Ambiente: ${environment === 'outdoor' ? 'Outdoor (sol natural)' : 'Indoor (controle)'}`}>
                                        <Typography variant="body2">{p.luzAmbiente}</Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                        </Grid>

                        {/* Aplicar ao controlador */}
                        {p.controller && <ApplyToDevice plant={p} mode={mode} vasoL={vasoL} environment={environment} deviceId={selectedDeviceId} />}
                    </>
                )}

                {/* Estado inicial */}
                {!result && !loading && !error && (
                    <Box sx={{ mt: 5, textAlign: 'center', opacity: 0.7 }}>
                        <FaRobot size={64} color="#4CAF50" />
                        <Typography variant="h6" sx={{ mt: 2 }}>O que você quer cultivar hoje?</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Digite o nome de uma planta acima e a IA do AutoGrow BR monta o guia completo para você.
                        </Typography>
                    </Box>
                )}
            </Container>

            {/* Footer */}
            <Box component="footer" sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <Container maxWidth="lg">
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                        <FaLeaf size={18} color="#4CAF50" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AutoGrow BR</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Guia de cultivo inteligente para pesquisar qualquer planta.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
}
